import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings, X, Eye, EyeOff, Plus, Pencil, Check } from 'lucide-react';

export interface CardConfig {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  type?: string; // Base card type (for multiple instances)
  instanceId?: string; // Unique instance identifier
}

interface SettingsMenuProps {
  cardConfigs: CardConfig[];
  onCardConfigChange: (configs: CardConfig[]) => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({ cardConfigs, onCardConfigChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editedName, setEditedName] = useState<string>('');

  const toggleCard = (cardId: string) => {
    const updatedConfigs = cardConfigs.map((config) =>
      config.id === cardId ? { ...config, enabled: !config.enabled } : config,
    );
    onCardConfigChange(updatedConfigs);
  };

  const startEditing = (cardId: string, currentName: string) => {
    setEditingCardId(cardId);
    setEditedName(currentName);
  };

  const saveEdit = (cardId: string) => {
    if (editedName.trim()) {
      const updatedConfigs = cardConfigs.map((config) =>
        config.id === cardId ? { ...config, name: editedName.trim() } : config,
      );
      onCardConfigChange(updatedConfigs);
    }
    setEditingCardId(null);
    setEditedName('');
  };

  const cancelEdit = () => {
    setEditingCardId(null);
    setEditedName('');
  };

  const addChartCard = () => {
    const chartCards = cardConfigs.filter(
      (config) => config.type === 'chart' || config.id === 'chart',
    );
    const nextNumber = chartCards.length + 1;
    const newCardId = `chart-${nextNumber}`;

    const newCard: CardConfig = {
      id: newCardId,
      name: `Chart ${nextNumber}`,
      description: `Additional chart for displaying different data`,
      enabled: true,
      type: 'chart',
      instanceId: `instance-${Date.now()}`,
    };

    onCardConfigChange([...cardConfigs, newCard]);
  };

  const removeCard = (cardId: string) => {
    const updatedConfigs = cardConfigs.filter((config) => config.id !== cardId);
    onCardConfigChange(updatedConfigs);
  };

  const resetToDefault = () => {
    const defaultConfigs = cardConfigs.map((config) => ({ ...config, enabled: true }));
    onCardConfigChange(defaultConfigs);
  };

  const enabledCount = cardConfigs.filter((config) => config.enabled).length;

  return (
    <>
      {/* Settings Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 whitespace-nowrap"
        title="Dashboard Settings"
      >
        <Settings className="h-3 w-3" />
        Cards ({enabledCount})
      </button>

      {/* Settings Modal */}
      {isOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Dashboard Settings
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="p-4 overflow-y-auto flex-1 min-h-0">
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Visible Cards
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Choose which cards to display on your dashboard. Changes are saved
                    automatically.
                  </p>
                </div>

                {/* Card Toggles */}
                <div className="space-y-2 mb-4">
                  {cardConfigs.map((config) => (
                    <div
                      key={config.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {editingCardId === config.id ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    saveEdit(config.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEdit();
                                  }
                                }}
                                className="flex-1 px-2 py-1 text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-pink-500 rounded focus:outline-none focus:ring-2 focus:ring-pink-500"
                                autoFocus
                              />
                              <button
                                onClick={() => saveEdit(config.id)}
                                className="p-1 text-green-500 hover:text-green-600 transition-colors"
                                title="Save"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1 text-gray-400 hover:text-gray-500 transition-colors"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {config.name}
                              </h4>
                              {(config.type === 'chart' || config.id === 'chart') && (
                                <button
                                  onClick={() => startEditing(config.id, config.name)}
                                  className="p-1 text-gray-400 hover:text-pink-500 transition-colors"
                                  title="Edit name"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                            </>
                          )}
                          {config.enabled ? (
                            <Eye className="h-4 w-4 text-green-500" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {config.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Remove button for additional chart cards */}
                        {config.type === 'chart' && config.id !== 'chart' && (
                          <button
                            onClick={() => removeCard(config.id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove Chart"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleCard(config.id)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:ring-offset-2 ${
                            config.enabled ? 'bg-pink-500' : 'bg-gray-200 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                              config.enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Chart Button */}
                <div className="mb-4 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <button
                    onClick={addChartCard}
                    className="w-full flex items-center justify-center gap-2 py-2 px-3 text-sm text-gray-600 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Chart Card
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={resetToDefault}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    Reset to Default
                  </button>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {enabledCount} of {cardConfigs.length} cards visible
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-b-xl flex-shrink-0">
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SettingsMenu;
